// ── Dashboard component domain types ──────────────────────────────────────

// Who a piece of data covers: the whole clinic, just the current user, or both.
export type DataScope = 'clinic' | 'user' | 'clinicUser';

export interface KpiCard {
  icon: string; // mat-icon name
  iconBg: string;
  iconColor: string;
  value: string;
  label: string;
  sub: string;
  trend: string;
  trendTone: 'up' | 'down' | 'flat';
  id?: string;
  loading?: boolean;
  valueLoading?: boolean;
  smallValue?: boolean;
  periodSelect?: 'billing' | 'storage';
  scope?: DataScope;
  updatedDaily?: boolean; // value comes from a daily cache, not live
  viewAllLink?: string; // when set, the card shows a "View all" link to this route
}

export interface ExamSlice {
  label: string;
  short: string;
  value: number;
  color: string;
  percent: number;
}

export interface RecentExam {
  initials: string;
  name: string;
  type: string;
  typeTag: 'teal' | 'blue' | 'orange';
  when: string;
  pid: number;
  examIndex: number;
  viewOnly: boolean;
  openerName: string | null;
}

export interface PatientLink {
  name: string;
  status: 'viewed' | 'sent' | 'unopened';
  text: string;
  type: 'consent' | 'exam';
  url: string;
}

export interface Invoice {
  initials: string;
  name: string;
  id: string;
  amount: string;
  rawAmount: number;
  status: 'paid' | 'due' | 'failed';
  typeName?: string;
  date?: string;
  rawDate?: Date;
  brand?: boolean;
  autoIndex?: number;
  copyLink?: string;
}

export interface Appointment {
  day: string;
  date: string;
  name: string;
  type: string;
  time: string;
}

export interface Birthday {
  pid: number;
  name: string;
  date: string;
  phone: string | null;
  email: string | null;
}

export interface UpcomingBirthRaw {
  name: string;
  edd: string; // formatted EDD
  daysLeft: number; // negative = overdue
  pid: number;
  phone: string | null;
  email: string | null;
}

export interface UpcomingBirth extends UpcomingBirthRaw {
  label: string; // 'Due today' | 'Due tomorrow' | 'In X days' | 'Overdue X day(s)'
}

export interface ResolvedFeature {
  key: string;
  category: string;
  label: string;
  labelHe: string | null;
  included: boolean;
  showOnDashboard: boolean; // belongs in the "What's Included" list
  suggestable: boolean; // upsell candidate for "Expand your plan" when not included
  requested: boolean; // this clinic already has an open request for it
  price: number | null; // upsell price, already gated by ShowPrice server-side (null = hide)
  sortOrder: number;
}

export interface FeatureGroup {
  category: string;
  label: string;
  features: ResolvedFeature[];
}

export interface PlanFeaturesApi {
  clinic: ResolvedFeature[] | null; // null for non-admins
  user: ResolvedFeature[];
}

// Category → display header + order for the "What's Included" grid.
export const FEATURE_GROUP_LABELS: Record<string, string> = {
  examType: 'Exam Types',
  general: 'General',
  followup: 'Follow-Up',
  administrative: 'Appointments & Billing',
  sms: 'SMS & Messaging',
  language: 'Language',
};

export const FEATURE_GROUP_ORDER = [
  'examType',
  'general',
  'followup',
  'administrative',
  'sms',
  'language',
];

// ── Lookup constants ───────────────────────────────────────────────────────

export const SLICE_COLORS = [
  '#015A6C',
  '#278EA1',
  '#64b5c0',
  '#90cdd4',
  '#b8dde0',
  '#2E7D9A',
  '#3A9CBB',
  '#4DB8D4',
  '#80CDD9',
  '#A3D9E1',
];

export const EXAM_TYPE_SHORT: Record<string, string> = {
  '1st Trimester': '1st',
  'First Trimester': '1st',
  'Nuchal Translucency': 'NT',
  'Early Scan': 'Early',
  'Early Anatomy': 'Early',
  'Early Anomaly': 'Early',
  'Late Anatomy': 'Late',
  'Late Anomaly': 'Late',
  'Detailed Anomaly': 'Detail',
  'Extended Anomaly': 'Ext',
  'Third Scan': '3rd',
  'Growth Assessment': 'Growth',
  'Weight Assessment': 'Weight',
  Gynecology: 'Gyn',
  Follicles: 'Foll',
};

export const EXAM_TYPE_TAG: Record<string, 'teal' | 'blue' | 'orange'> = {
  'Nuchal Translucency': 'teal',
  'Third Scan': 'blue',
  'Early Scan': 'teal',
  'Detailed Anomaly': 'orange',
  'Extended Anomaly': 'orange',
  'Growth Assessment': 'teal',
  'Weight Assessment': 'teal',
};

export const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export const DAY_ABBR = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
